import React, { useEffect, useRef } from "react";
import "../../styles/about.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const AboutSection = () => {
  // 1. Create a ref to hold the section element for GSAP context
  const sectionRef = useRef(null);

  useEffect(() => {
    // 2. Create a GSAP context to manage and revert animations easily
    const context = gsap.context(() => {
      // Collect all text elements that need the reveal animation
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content"),
      );

      // --- 1. Scrubbed Text Reveal Animation (Merise.ae style) ---
      textElements.forEach((element, index) => {
        // Set Initial State: Hidden (opacity: 0), slightly down (y: 25), and clipped from the bottom
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            // Trigger the animation when the parent div of the text enters the viewport
            trigger: element.closest("div"),
            start: "top 85%", // Animation starts when the top of the trigger is 85% down the viewport
            end: "center 60%", // Animation ends when the center of the trigger is 60% down the viewport
            scrub: 1, // Smooth link to scroll position
            // markers: true, // Uncomment for debugging scroll positions
          },
        });

        // Define the Target State: Visible (opacity: 1), original position (y: 0), and fully unclipped
        tl.to(element, {
          y: 0, // Final state: Original position (move up)
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out", // Smooth movement
          delay: index * 0.1, // Stagger effect for line-by-line reveal
        });
      });

      // --- 2. Scrubbed Divider Line Animation (Grow from Left) ---
      // NOTE: For this part, I will animate the horizontal and vertical dividers.
      // The original code targeted a single '.about-divider-line'. I will adapt
      // the animation to the existing border-like elements.

      const horizontalDivider = sectionRef.current.querySelector(
        ".horizontal-divider",
      );
      const verticalDivider =
        sectionRef.current.querySelector(".vertical-divider");

      // 2a. Animate Horizontal Divider (Full width)
      if (horizontalDivider) {
        gsap.set(horizontalDivider, { scaleX: 0, transformOrigin: "left" });

        gsap.to(horizontalDivider, {
          scaleX: 1, // Grow to full width
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: horizontalDivider, // Trigger on the horizontal line itself
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      }

      // 2b. Animate Vertical Divider (Full height - centered)
      if (verticalDivider) {
        gsap.set(verticalDivider, { scaleY: 0, transformOrigin: "center" });

        gsap.to(verticalDivider, {
          scaleY: 1, // Grow to full height
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: verticalDivider, // Trigger on the vertical line itself
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      }
    }, sectionRef); // Pass the ref to the context

    // Cleanup function to revert all ScrollTriggers and animations when component unmounts
    return () => context.revert();
  }, []);

  return (
    <>
      <section
        // Attach the ref here
        ref={sectionRef}
        className="w-full bg-background-dark pb-[150px] md:pb-[308px] rounded-b-[60px] sm:rounded-b-[80px] xl:rounded-b-[1000px] "
      >
        <div className="w-full max-w-[1620px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* About Description */}
          <div className="text-center mb-[40px] sm:mb-[60px] lg:mb-[138px]">
            <h2 className="bottom-text text-[40px] sm:text-[60px] md:text-[70px] xl:text-xl font-bold leading-[48px] sm:leading-[72px] md:leading-[84px] xl:leading-mega text-white font-['Parkinsans']">
              About Utpann
            </h2>
            {/* Added animated-text-content class to the paragraph */}
            <p className="animated-text-content text-[18px] lg:text-sm font-light  text-white mx-auto mt-[20px] ">
              UTPANN, the flagship brand of Govardhanathji Energies LLP,
              Gujarat's largest biogas-based fertilizer producer, is dedicated
              to enriching Indian soil through sustainable, organic solutions.
              The brand offers high-quality FOM, LFOM, PROM, and 11
              Bio-Fertilizers all approved under FCO-1985, ensuring purity and
              effectiveness for farmers. With a strong network of over 244+
              authorized distributors, UTPANN promotes eco-friendly farming
              practices and soil rejuvenation through awareness programs.
            </p>
          </div>

          {/* Features Grid - Modified for Border Style */}
          <div className="relative mb-[40px] sm:mb-[60px] lg:mb-[68px] ">
            {/* Horizontal and Vertical Dividers for the 4-box layout */}
            <div className="absolute inset-0 hidden md:block z-0">
              {/* Vertical Divider - Added vertical-divider class */}
              <div className="bg-border-hori-color absolute left-1/2 top-0 bottom-0 w-[4px] transform -translate-x-1/2 vertical-divider"></div>
              {/* Horizontal Divider - Added horizontal-divider class */}
              <div className="bg-boredr-color absolute top-1/2 left-0 right-0 h-[4px] transform -translate-y-1/2 horizontal-divider"></div>
            </div>

            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 mx-auto gap-[30px] md:gap-[0px] lg:gap-[0px]">
              {/* Feature Box 1 */}
              <div className="text-center space-y-2 lg:space-y-[10px] md:p-[60px]">
                {/* Added animated-text-content class */}
                <h3 className="animated-text-content text-[20px] sm:text-[32px] lg:text-md font-bold leading-[32px] sm:leading-[44px] lg:leading-extra text-white font-['Parkinsans']">
                  Promotes Organic Farming
                </h3>
                {/* Added animated-text-content class */}
                <p className="animated-text-content text-[18px] lg:text-sm font-light leading-loose text-white max-w-[501px] mx-auto">
                  Encouraging farmers to adopt natural, chemical-free
                  cultivation for healthier crops and environment.
                </p>
              </div>

              {/* Feature Box 2 */}
              <div className="text-center space-y-2 lg:space-y-[10px] md:p-[60px]">
                {/* Added animated-text-content class */}
                <h3 className="animated-text-content text-[20px] sm:text-[32px] lg:text-md font-bold leading-[32px] sm:leading-[44px] lg:leading-extra text-white font-['Parkinsans']">
                  Restores Soil Health
                </h3>
                {/* Added animated-text-content class */}
                <p className="animated-text-content text-[18px] lg:text-sm font-light leading-loose text-white max-w-[501px] mx-auto">
                  Enriches soil with organic nutrients, improving fertility,
                  structure, and long-term productivity.
                </p>
              </div>

              {/* Feature Box 3 */}
              <div className="text-center space-y-2 lg:space-y-[10px] md:p-[60px]">
                {/* Added animated-text-content class */}
                <h3 className="animated-text-content text-[20px] sm:text-[32px] lg:text-md font-bold leading-[32px] sm:leading-[44px] lg:leading-extra text-white font-['Parkinsans']">
                  Pure & Natural Quality
                </h3>
                {/* Added animated-text-content class */}
                <p className="animated-text-content text-[18px] lg:text-sm font-light leading-loose text-white max-w-[501px] mx-auto">
                  Produced from biogas by-products under strict FCO-1985
                  standards to ensure consistency and purity.
                </p>
              </div>

              {/* Feature Box 4 */}
              <div className="text-center space-y-2 lg:space-y-[10px] md:p-[60px]">
                {/* Added animated-text-content class */}
                <h3 className="animated-text-content text-[20px] sm:text-[32px] lg:text-md font-bold leading-[32px] sm:leading-[44px] lg:leading-extra text-white font-['Parkinsans']">
                  Saves Earth's Future
                </h3>
                {/* Added animated-text-content class */}
                <p className="animated-text-content text-[18px] lg:text-sm font-light leading-loose text-white  max-w-[501px] mx-auto">
                  A sustainable farming solution that protects soil, water, and
                  biodiversity for generations ahead.
                </p>
              </div>
            </div>
          </div>

          <div className="relative mt-[68px]">
            {/* This image is not being animated in the provided code */}
            <img
              src="/assets/abouimg/aboutbottomImg.png"
              alt="Utpann Kesari Phos Product"
              className="w-auto h-auto object-contain"
            />
          </div>
        </div>
      </section>
      {/* Bottom Image */}
      <div className="flex item-center justify-center z-20 mt-[-154px] sm:mt-[-200px] lg:mt-[-408px]">
        {/* This image is not being animated in the provided code */}
        <img
          src="/assets/abouimg/aboutPlantBottom.png"
          alt="Bottom Decorative Image"
          className="h-auto object-contain"
        />
      </div>
    </>
  );
};

export default AboutSection;
